/**
 * useGlobalBanners — domain-agnostic keyed banner store.
 *
 * Module-scope singleton: every caller shares the same reactive list.
 * push({ key, ...rest }) replaces any existing banner with the same key.
 * clear(key) removes a banner by key.
 *
 * NO domain imports — this composable is intentionally dependency-free
 * (only vue) to satisfy the SRP/SSOT constraint.
 */
import { ref, computed } from 'vue'

export interface GlobalBanner {
  key: string
  [k: string]: unknown
}

// Module-scope singleton store.
const _banners = ref<GlobalBanner[]>([])

export function useGlobalBanners() {
  const list = computed(() => _banners.value)

  function push(banner: GlobalBanner): void {
    const idx = _banners.value.findIndex((b) => b.key === banner.key)
    if (idx >= 0) {
      _banners.value.splice(idx, 1, banner)
    } else {
      _banners.value.push(banner)
    }
  }

  function clear(key: string): void {
    _banners.value = _banners.value.filter((b) => b.key !== key)
  }

  /** Reset the store to empty — use in afterEach to avoid cross-test leakage. */
  function _reset(): void {
    _banners.value = []
  }

  return { list, push, clear, _reset }
}
